import { DateTime } from 'luxon'

export default {
  install: (app) => {
    app.config.globalProperties.$helpers = {
      // Base URL for a website's own domain (its CMS, public pages, exports).
      //
      // Sites are served over https on their real hostname in every deployed
      // environment, but locally the websites app is published on a plain http
      // port (e.g. http://vale.test:8002), so a hardcoded https:// link just
      // fails to connect. VITE_WEBSITE_BASE_URL overrides the scheme/port for
      // local dev: set it to the websites app's origin and {host} is replaced
      // with the site's hostname.
      websiteUrl(host, path = '') {
        const override = import.meta.env.VITE_WEBSITE_BASE_URL
        const base = override ? override.replace('{host}', host) : `https://${host}`

        return `${base.replace(/\/$/, '')}${path}`
      },
      // Which of a site's hostnames to link to.
      //
      // Deployed, that is the real domain: it is what the site is served on
      // and what an admin expects to land on. Locally it is the internal URL,
      // because a site's `primary_domain` points at whichever environment it
      // was last set up in — a site restored from UAT carries a
      // `*.uat.nurtur.digital` hostname that does not resolve on a developer's
      // machine, so every link built from it fails to connect.
      //
      // Keyed on VITE_WEBSITE_BASE_URL being set, which is what marks a local
      // websites app in the first place.
      websiteHost(site) {
        const local = Boolean(import.meta.env.VITE_WEBSITE_BASE_URL)

        return local
          ? site?.internal_url || site?.primary_domain || null
          : site?.primary_domain || site?.internal_url || null
      },
      // The F&C annual review 'Upper Quartile' price points, matching the CAT's price filters. Regional averages
      // are entered for each of them.
      reviewPricePoints() {
        return [500000, 600000, 750000, 1000000, 2000000, 3000000]
      },
      moveItemUp(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index - 1, 0, item)
      },
      moveItemDown(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index + 1, 0, item)
      },
      removeItem(item, collection) {
        const index = collection.indexOf(item)
        if (index >= 0) {
          collection.splice(collection.indexOf(item), 1)
        }
      },
      // Returns black or white, whichever is the more readable on top of the given hex colour.
      readableTextColour(hex) {
        hex = String(hex || '').replace('#', '')

        if (hex.length === 3) {
          hex = hex
            .split('')
            .map((char) => char + char)
            .join('')
        }

        if (hex.length !== 6) {
          return '#FFFFFF'
        }

        // Relative luminance as per the WCAG contrast definition.
        const channels = [0, 2, 4].map((i) => {
          const channel = parseInt(hex.substr(i, 2), 16) / 255

          return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
        })

        const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]

        return luminance > 0.179 ? '#000000' : '#FFFFFF'
      },
      isValidUrl(url) {
        return /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(:[0-9]+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/.test(
          url
        )
      },
      sanitiseVideoUrl(url) {
        let matches = /^https?:\/\/(?:www.)?(?:youtu.be\/|youtube.com\/watch\?.*v=)([a-zA-z0-9_-]+)/.exec(url)
        if (matches?.length > 0) {
          return `https://www.youtube.com/embed/${matches[1]}`
        }
        matches =
          /^https?:\/\/(?:www.|player.)?vimeo.com(?:\/.*\/review|\/.*\/download|\/video|\/external)?\/(\d*)/.exec(url)
        if (matches?.length > 0) {
          return `https://player.vimeo.com/video/${matches[1]}`
        }

        return url
      },
      isBefore(date1, date2, inclusive = false) {
        if (!date1 || !date2) {
          return false
        }

        date1 = DateTime.fromISO(date1)
        date2 = DateTime.fromISO(date2)

        return inclusive ? date1 <= date2 : date1 < date2
      },
      isAfter(date1, date2, inclusive = false) {
        if (!date1 || !date2) {
          return false
        }

        date1 = DateTime.fromISO(date1)
        date2 = DateTime.fromISO(date2)

        return inclusive ? date1 >= date2 : date1 > date2
      },
      isPast(date) {
        return date && DateTime.fromISO(date) <= DateTime.utc()
      },
      isFuture(date) {
        return date && DateTime.fromISO(date) > DateTime.utc()
      },
      dateDiff(date1, date2 = 'now') {
        if (!date1) {
          return 0
        }

        date1 = DateTime.fromISO(date1)
        date2 = date2 === 'now' ? DateTime.utc() : DateTime.fromISO(date2)

        return Math.abs(Math.round(date1.diff(date2, 'seconds').toObject().seconds))
      }
    }
  }
}
